/**
 * Authentication Constants
 *
 * Central location for authentication-related constants.
 * Used across server actions, middleware, and components.
 */

/**
 * Default timeout for authentication API requests in milliseconds.
 * Used for login, signup, password reset, and token verification requests.
 */
export const AUTH_REQUEST_TIMEOUT_MS = 15000;

/**
 * Error messages for authentication flows
 */
export const AUTH_ERROR_MESSAGES = {
  SESSION_EXPIRED: "Your session has expired. Please sign in again.",
  NETWORK_ERROR: "An unexpected error occurred. Please try again.",
  TIMEOUT_LOGIN:
    "Login request timed out. Please check your connection and try again.",
  TIMEOUT_SIGNUP:
    "Signup request timed out. Please check your connection and try again.",
  TIMEOUT_PASSWORD_RECOVERY:
    "Request timed out. Please check your connection and try again.",
  TIMEOUT_PASSWORD_RESET:
    "Password reset request timed out. Please check your connection and try again.",
  TIMEOUT_TOKEN_VERIFICATION:
    "Token verification request timed out. Please check your connection and try again.",
  INVALID_RESPONSE: "Authentication failed - invalid response from server",
  INVITATION_TOKEN_REQUIRED: "Invitation token is required",
  INVITATION_TOKEN_MISSING:
    "No invitation token found. Please use the link from your invitation email.",
  INVITATION_TOKEN_INVALID:
    "This invitation link has expired or is no longer valid. Please contact your administrator for a new invitation.",
} as const;

/**
 * Success messages for authentication flows
 */
export const AUTH_SUCCESS_MESSAGES = {
  LOGIN: "Login successful",
  SIGNUP_REDIRECT: "Account created successfully. Please sign in.",
  PASSWORD_RESET_REDIRECT:
    "Password reset successfully. Please sign in with your new password.",
  SIGNOUT_REDIRECT: "You have been signed out successfully.",
  INVITATION_VERIFIED: "Invitation verified successfully",
} as const;

/**
 * Copy sign-in may toast from `?message=`. Signup, password reset, and
 * sign-out redirects use these strings. Any other value is ignored.
 */
export const SIGNIN_URL_SUCCESS_MESSAGES = [
  AUTH_SUCCESS_MESSAGES.SIGNUP_REDIRECT,
  AUTH_SUCCESS_MESSAGES.PASSWORD_RESET_REDIRECT,
  AUTH_SUCCESS_MESSAGES.SIGNOUT_REDIRECT,
] as const;

export type SignInUrlSuccessMessage =
  (typeof SIGNIN_URL_SUCCESS_MESSAGES)[number];

export function signInUrlSuccessMessage(
  value: string | null | undefined,
): SignInUrlSuccessMessage | null {
  if (
    value &&
    (SIGNIN_URL_SUCCESS_MESSAGES as readonly string[]).includes(value)
  ) {
    return value as SignInUrlSuccessMessage;
  }
  return null;
}

/**
 * URL error codes that should surface as form errors on the sign-in page.
 * Maps ?error=<code> values to user-facing messages. Codes not in this
 * map are intentionally ignored — they represent navigation signals
 * (proxy redirects, error boundaries) where the sign-in form itself is
 * already sufficient context.
 */
export const SIGNIN_URL_ERROR_MESSAGES: Record<string, string> = {
  SessionExpired: AUTH_ERROR_MESSAGES.SESSION_EXPIRED,
  session_expired: AUTH_ERROR_MESSAGES.SESSION_EXPIRED,
  page_not_ready: "The page was still loading. Please try again.",
};

/**
 * Public authentication pages. Signed-in users are bounced away from these
 * by the proxy, and native form submits are only ever redirected back here.
 */
export const AUTH_PAGE_PATHS = [
  "/signin",
  "/signup",
  "/forgot-password",
  "/reset-password",
] as const;

export type AuthPagePath = (typeof AUTH_PAGE_PATHS)[number];

export function isAuthPagePath(value: unknown): value is AuthPagePath {
  return (
    typeof value === "string" &&
    (AUTH_PAGE_PATHS as readonly string[]).includes(value)
  );
}

/**
 * Password recovery message (intentionally vague for security)
 */
export const getPasswordRecoveryMessage = (email: string): string =>
  `If an account exists for ${email}, we have sent password reset instructions.`;
